import React from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { UpdateUserModel3DDocument } from "../../gql/userModel3D";
import { getUserModelArchitectureSymbolOptions } from "../../features/floorplan3d/userModels/userModel3DArchitectureSymbols";
import {
  getUserModel3DGeneratedFootprintPath,
  getUserModel3DPlanSvgPath,
} from "../../features/floorplan3d/userModels/userModel3DPlanSvg";
import {
  getUserModel3DTitle,
  UserModel3DLibraryItem,
} from "../../features/floorplan3d/userModels/userModel3DTypes";
import {
  commerceFormFromModel,
  parseCommerceFormForUpdate,
  UserModel3DCommerceFormState,
} from "../../features/floorplan3d/userModels/userModel3DCommerce";
import { floorplannerStore } from "../../store/floorplannerStore";
import { convertMillimeterToWorld, convertWorldToMillimeter } from "../../utils/conversions";
import { UserModel3DCommerceFields } from "./UserModel3DCommerceFields";

type Props = {
  model: UserModel3DLibraryItem;
  onClose: () => void;
  onSaved: (model: UserModel3DLibraryItem) => void;
};

const toMillimeters = (meters?: number | null) =>
  convertWorldToMillimeter(meters && meters > 0 ? meters : 0);

export const EditUserModel3DModal: React.FC<Props> = ({
  model,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const [name, setName] = React.useState(() => getUserModel3DTitle(model));
  const [widthMm, setWidthMm] = React.useState(
    String(
      toMillimeters(
        model.dimensions?.width ?? model.bounds?.size?.width ?? 1,
      ),
    ),
  );
  const [depthMm, setDepthMm] = React.useState(
    String(
      toMillimeters(
        model.dimensions?.depth ?? model.bounds?.size?.depth ?? 0.8,
      ),
    ),
  );
  const [heightMm, setHeightMm] = React.useState(
    String(
      toMillimeters(
        model.dimensions?.height ?? model.bounds?.size?.height ?? 0.8,
      ),
    ),
  );
  const [iconMode, setIconMode] = React.useState<
    "generated" | "architectureSymbol"
  >(model.architectureSymbolKey ? "architectureSymbol" : "generated");
  const [architectureSymbolKey, setArchitectureSymbolKey] = React.useState(
    model.architectureSymbolKey ?? "",
  );
  const [commerceForm, setCommerceForm] =
    React.useState<UserModel3DCommerceFormState>(() =>
      commerceFormFromModel(model, floorplannerStore.currency),
    );
  const [symbolQuery, setSymbolQuery] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [updateUserModel3D, updateState] = useMutation(
    UpdateUserModel3DDocument,
  );
  /** Size is only durable after processing; the worker overwrites dimensions until then. */
  const canEditDimensions = model.status === "ready";
  const symbolOptions = React.useMemo(
    () => getUserModelArchitectureSymbolOptions(),
    [],
  );
  const filteredSymbols = React.useMemo(() => {
    const query = symbolQuery.trim().toLowerCase();
    if (!query) return symbolOptions;
    return symbolOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.category.toLowerCase().includes(query) ||
        option.id.toLowerCase().includes(query),
    );
  }, [symbolOptions, symbolQuery]);
  const selectedSymbol = symbolOptions.find(
    (option) => option.id === architectureSymbolKey,
  );
  const generatedPreview = getUserModel3DGeneratedFootprintPath(model);
  const selectedPreview = architectureSymbolKey
    ? getUserModel3DPlanSvgPath({
        ...model,
        architectureSymbolKey,
      })
    : generatedPreview;

  React.useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const parseDimensionMm = (value: string, label: string) => {
    const number = Number(value);
    if (!Number.isFinite(number) || number < 10 || number > 50000) {
      throw new Error(t("userModels3d.dimensionRangeError", { label }));
    }
    return convertMillimeterToWorld(number);
  };

  const save = async () => {
    setError(null);
    const nextName = name.trim();
    if (!nextName) {
      setError(t("userModels3d.nameRequired"));
      return;
    }
    try {
      const nextSymbolKey =
        iconMode === "architectureSymbol" ? architectureSymbolKey.trim() : "";
      if (iconMode === "architectureSymbol" && !nextSymbolKey) {
        setError(t("userModels3d.chooseSymbolError"));
        return;
      }
      const dimensionVariables = canEditDimensions
        ? {
            width: parseDimensionMm(widthMm, t("propertyPanel.width")),
            depth: parseDimensionMm(depthMm, t("propertyPanel.depth")),
            height: parseDimensionMm(heightMm, t("propertyPanel.height")),
          }
        : {};
      const commerceVariables = parseCommerceFormForUpdate(commerceForm);
      const result = await updateUserModel3D({
        variables: {
          id: model.id,
          name: nextName,
          ...dimensionVariables,
          architectureSymbolKey: nextSymbolKey,
          ...commerceVariables,
        },
      });
      const updated = result.data?.updateUserModel3D as
        | UserModel3DLibraryItem
        | undefined;
      if (!updated) {
        throw new Error(t("userModels3d.saveError"));
      }
      onSaved(updated);
      onClose();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : t("userModels3d.saveError"),
      );
    }
  };

  return createPortal(
    <div
      data-user-model-3d-edit-modal=""
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 p-3 sm:p-6"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget && !updateState.loading) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-user-model-3d-title"
        tabIndex={-1}
        className="relative max-h-[calc(100vh-24px)] w-full max-w-lg overflow-y-auto rounded-2xl bg-[#F8F7F5] p-5 shadow-2xl focus:outline-none sm:p-7"
      >
        <button
          type="button"
          aria-label={t("common.close")}
          disabled={updateState.loading}
          onClick={onClose}
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#111] shadow-sm hover:bg-[#f1efe8] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-50"
        >
          <IoClose size={20} />
        </button>
        <h2
          id="edit-user-model-3d-title"
          className="pr-12 text-xl font-semibold tracking-[-0.02em] text-[#111]"
        >
          {t("userModels3d.editTitle")}
        </h2>
        <div className="mt-5 space-y-5">
          <label className="block">
            <span className="text-sm font-medium text-[#272622]">
              {t("common.name")}
            </span>
            <input
              type="text"
              value={name}
              maxLength={120}
              disabled={updateState.loading}
              onChange={(event) => setName(event.target.value)}
              className="mt-2 h-11 w-full rounded-lg border border-[#c9c6be] bg-white px-3 text-sm text-[#111] outline-none focus:border-[#111] focus:ring-1 focus:ring-[#111] disabled:opacity-60"
            />
          </label>
          <UserModel3DCommerceFields
            value={commerceForm}
            onChange={setCommerceForm}
            disabled={updateState.loading}
          />
          <fieldset className="space-y-2" disabled={!canEditDimensions}>
            <legend className="text-sm font-medium text-[#272622]">
              {t("userModels3d.dimensionsLegend")}
            </legend>
            <div className="grid grid-cols-3 gap-2">
              <label className="block">
                <span className="text-[11px] font-semibold uppercase text-gray-600">
                  {t("propertyPanel.width")}
                </span>
                <input
                  type="number"
                  min={10}
                  max={50000}
                  step={1}
                  value={widthMm}
                  disabled={!canEditDimensions || updateState.loading}
                  onChange={(event) => setWidthMm(event.target.value)}
                  className="mt-1 h-11 w-full rounded-lg border border-[#c9c6be] bg-white px-2 text-sm text-[#111] outline-none focus:border-[#111] focus:ring-1 focus:ring-[#111] disabled:bg-gray-100 disabled:text-gray-500"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase text-gray-600">
                  {t("propertyPanel.depth")}
                </span>
                <input
                  type="number"
                  min={10}
                  max={50000}
                  step={1}
                  value={depthMm}
                  disabled={!canEditDimensions || updateState.loading}
                  onChange={(event) => setDepthMm(event.target.value)}
                  className="mt-1 h-11 w-full rounded-lg border border-[#c9c6be] bg-white px-2 text-sm text-[#111] outline-none focus:border-[#111] focus:ring-1 focus:ring-[#111] disabled:bg-gray-100 disabled:text-gray-500"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase text-gray-600">
                  {t("propertyPanel.height")}
                </span>
                <input
                  type="number"
                  min={10}
                  max={50000}
                  step={1}
                  value={heightMm}
                  disabled={!canEditDimensions || updateState.loading}
                  onChange={(event) => setHeightMm(event.target.value)}
                  className="mt-1 h-11 w-full rounded-lg border border-[#c9c6be] bg-white px-2 text-sm text-[#111] outline-none focus:border-[#111] focus:ring-1 focus:ring-[#111] disabled:bg-gray-100 disabled:text-gray-500"
                />
              </label>
            </div>
            <p className="text-xs text-[#625f58]">
              {canEditDimensions
                ? t("userModels3d.dimensionsHint")
                : t("userModels3d.dimensionsLockedUntilReady")}
            </p>
          </fieldset>
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-[#272622]">
              {t("userModels3d.iconLegend")}
            </legend>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-[#3f3d38]">
              <input
                type="radio"
                name="user-model-icon"
                checked={iconMode === "generated"}
                onChange={() => setIconMode("generated")}
                className="mt-1"
              />
              <span>
                <span className="block font-medium">
                  {t("userModels3d.generatedFootprint")}
                </span>
                <img
                  alt=""
                  src={generatedPreview}
                  className="mt-2 h-16 w-24 rounded border border-[#dfe5e2] bg-white object-contain p-1"
                />
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-[#3f3d38]">
              <input
                type="radio"
                name="user-model-icon"
                checked={iconMode === "architectureSymbol"}
                onChange={() => setIconMode("architectureSymbol")}
                className="mt-1"
              />
              <span className="block font-medium">
                {t("userModels3d.standardSymbol")}
              </span>
            </label>
            {iconMode === "architectureSymbol" && (
              <div className="space-y-2 pl-6">
                <input
                  value={symbolQuery}
                  onChange={(event) => setSymbolQuery(event.target.value)}
                  placeholder={t("userModels3d.searchSymbols")}
                  className="h-10 w-full rounded-lg border border-[#c9c6be] bg-white px-3 text-sm outline-none focus:border-[#111] focus:ring-1 focus:ring-[#111]"
                />
                {selectedSymbol && (
                  <div className="flex items-center gap-2 rounded-md border border-[#e8dca7] bg-[#fbf8e8] px-2 py-1.5 text-xs">
                    <img
                      src={selectedPreview}
                      alt=""
                      className="h-8 w-8 object-contain"
                    />
                    {selectedSymbol.label}
                  </div>
                )}
                <div className="max-h-48 overflow-y-auto rounded-md border border-[#dfe5e2] bg-white">
                  {filteredSymbols.length === 0 ? (
                    <p className="px-3 py-4 text-center text-xs text-gray-500">
                      {t("userModels3d.noMatchingSymbols")}
                    </p>
                  ) : (
                    filteredSymbols.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setArchitectureSymbolKey(option.id)}
                        className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-gray-50 ${
                          architectureSymbolKey === option.id
                            ? "bg-[#fbf8e8]"
                            : ""
                        }`}
                      >
                        <img
                          src={
                            option.svgOptions?.svgPath || option.src
                          }
                          alt=""
                          className="h-8 w-8 shrink-0 object-contain"
                        />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-gray-900">
                            {option.label}
                          </span>
                          <span className="block truncate text-xs text-gray-500">
                            {option.category}
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </fieldset>
          {error && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              {error}
            </div>
          )}
          <div className="space-y-2">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={updateState.loading}
                className="h-11 rounded-lg border border-[#bdb9b0] bg-white px-4 text-sm font-semibold text-[#302f2b] hover:border-[#77736a] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                disabled={!name.trim() || updateState.loading}
                onClick={() => void save()}
                className="h-11 rounded-lg bg-boniYellowPrimary px-5 text-sm font-semibold text-black hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                {updateState.loading
                  ? t("common.saving")
                  : t("userModels3d.saveChanges")}
              </button>
            </div>
            <div className="flex items-start gap-2 rounded-lg border border-[#e8e5dc] bg-[#f7f6f0] px-3 py-2.5">
              <FiInfo
                size={14}
                className="mt-0.5 shrink-0 text-[#625f58]"
                aria-hidden
              />
              <p className="text-xs leading-4 text-[#625f58]">
                {t("userModels3d.saveEffectsHint")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
